import { forwardRef } from "react";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatListDate, listCorrespondent, listSecondaryLine, subjectOrPlaceholder } from "../mailFormat";
import { MailPill } from "./MailPill";
import { MailTaskStatusBadge } from "./MailTaskStatusBadge";
import type { MailSummary } from "../types";

type MailListItemProps = {
  mail: MailSummary;
  isSelected: boolean;
  /** The one option reachable with Tab: the selected message, or the first when none is. */
  isTabStop: boolean;
  /** Show which customers the message is filed against, for lists that span customers. */
  showCustomers: boolean;
  onSelect: (mailId: string) => void;
};

const DirectionIcon = ({ mail }: { mail: MailSummary }) => {
  if (mail.direction === "outbound") {
    return <ArrowUpRight className="size-3.5 shrink-0 text-indigo-500" aria-label="Sent" />;
  }
  if (mail.direction === "inbound") {
    return <ArrowDownLeft className="size-3.5 shrink-0 text-emerald-600" aria-label="Received" />;
  }
  return null;
};

export const MailListItem = forwardRef<HTMLLIElement, MailListItemProps>(({ mail, isSelected, isTabStop, showCustomers, onSelect }, ref) => {
  const secondary = listSecondaryLine(mail);

  return (
    <li
      ref={ref}
      role="option"
      aria-selected={isSelected}
      tabIndex={isTabStop ? 0 : -1}
      data-mail-id={mail.id}
      onClick={() => onSelect(mail.id)}
      className={cn(
        "scroll-mt-10 cursor-pointer border-b border-l-4 border-b-gray-100 px-3 py-2.5 outline-none",
        "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        isSelected ? "border-l-sky-600 bg-sky-50" : "border-l-transparent hover:bg-gray-50",
      )}
    >
      <div className="flex items-center gap-1.5">
        <DirectionIcon mail={mail} />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
          {listCorrespondent(mail)}
        </span>
        {mail.messageCount > 1 && (
          <span
            className="shrink-0 rounded-full bg-sky-100 px-1.5 text-[11px] font-semibold leading-4 text-sky-700"
            aria-label={`${mail.messageCount} emails in this conversation`}
          >
            {mail.messageCount}
          </span>
        )}
        <time dateTime={mail.dateReceivedUtc} className="shrink-0 text-xs text-gray-500">
          {formatListDate(mail.dateReceivedUtc)}
        </time>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate text-sm text-gray-800">{subjectOrPlaceholder(mail.subject)}</span>
        <MailTaskStatusBadge status={mail.taskStatus} />
      </div>
      {secondary && <div className="truncate text-xs text-gray-500">{secondary}</div>}
      {showCustomers && mail.customers.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {mail.customers.map(customer => (
            <MailPill key={customer.id} tone="customer">
              {customer.name ?? "Customer"}
            </MailPill>
          ))}
        </div>
      )}
    </li>
  );
});

MailListItem.displayName = "MailListItem";
